import React from 'react';
import Hero from '@/components/Hero';
import ContactForm from '@/components/contact/ContactForm';
import FAQItem from '@/components/FAQItem';
import FloatingHelpButton from '@/components/FloatingHelpButton';
import StickyBookingButton from '@/components/StickyBookingButton';
import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/common/SEOMetadata';
import { Mail, Instagram, Calendar } from 'lucide-react';

const Contact = () => {
  const faqs = [
    {
      question: "What should I bring on the tour?",
      answer: "Comfortable walking shoes, a water bottle, and an umbrella if rain is expected. Don't forget your camera to capture the charming sights and stories of our cat-filled adventure. If you're able to, bring your cat! In a carrier or a backpack. What ever you're comfortable with! Most importantly, bring a curious spirit and love of cats."
    },
    {
      question: "Is the tour suitable for children?",
      answer: "Yes, the tour is family-friendly and suitable for pre-teens and teenagers who will appreciate hearing about the adventurous cats of New York City. However, please note that the tour involves walking for about 1.5 hours, so ensure everyone is up for the journey."
    },
    {
      question: "What happens if it rains?",
      answer: "The tour will proceed in light rain, so please bring an umbrella or a raincoat. In case of severe weather, we will reschedule or refund your booking to ensure everyone's safety and comfort. We'll notify you via email in advance if the tour is canceled due to bad weather."
    },
    {
      question: "What kind of cats will we learn about?",
      answer: "You'll hear about a variety of legendary cats, from heroic mousers and beloved mascots to theater cats and resident apartment cats. Each has left a unique paw print on the history of New York City."
    },
    {
      question: "Do I need to book in advance?",
      answer: "Yes, we recommend booking in advance to secure your spot, as tours can fill up quickly. You can easily book your tour through our website."
    },
    {
      question: "Can I take photos during the tour?",
      answer: "Absolutely! Photos are encouraged, but please be respectful and do not use flash photography around the cats. However, we ask that you refrain from taking videos during the tour to ensure the experience is enjoyable for everyone."
    }
  ];

  return (
    <>
      <SEOMetadata
        title="Contact Us"
        description="Get in touch with Cats About Town Tours for questions, special requests, or to book your NYC cat history tour."
        canonicalUrl="https://catsabouttowntours.com/contact"
        ogImage="/lovable-uploads/f7592fba-8c89-4188-b2ba-0fdb8878d257.png"
        ogImageAlt="Contact Cats About Town Tours"
        keywords={[
          'contact cats about town',
          'nyc cat tour contact',
          'book cat tour',
          'cat walking tour questions'
        ]}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://catsabouttowntours.com/" },
              { "@type": "ListItem", "position": 2, "name": "Contact Us", "item": "https://catsabouttowntours.com/contact" }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen pt-20">
        {/* Hero Section - Updated with new image */}
        <Hero
          title="How can we help?"
          subtitle="We're available to chat!"
          backgroundImage="/lovable-uploads/f7592fba-8c89-4188-b2ba-0fdb8878d257.png"
          height="medium"
          backgroundAlt="Contact Cats About Town Tours"
        />
        
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <ContactForm />
              
              {/* Contact Info */}
              <div className="space-y-8">
                {/* Useful Resources Card (Replacing Meeting Point) */}
                <div className="premium-card border border-gold/40 hover:shadow-md transition-all duration-300">
                  <h3 className="text-2xl font-montserrat font-bold mb-4">Useful Resources</h3>
                  <div className="space-y-4">
                    <a 
                      href="/blog" 
                      className="block p-4 rounded-md bg-neutral-tan hover:bg-neutral-tan/80 transition-all duration-200"
                    >
                      <p className="font-bold text-lg mb-1 font-montserrat">The Cat History Blog</p>
                      <p className="text-text/80 font-montserrat">Read fascinating stories about NYC's cats</p>
                    </a>
                    
                    <a 
                      href="/tours" 
                      className="block p-4 rounded-md bg-neutral-tan hover:bg-neutral-tan/80 transition-all duration-200"
                    >
                      <p className="font-bold text-lg mb-1 font-montserrat">Tour Information</p>
                      <p className="text-text/80 font-montserrat">Learn about our different tour options</p>
                    </a>
                    
                    <a 
                      href="/in-the-news" 
                      className="block p-4 rounded-md bg-neutral-tan hover:bg-neutral-tan/80 transition-all duration-200"
                    >
                      <p className="font-bold text-lg mb-1 font-montserrat">Press Coverage</p>
                      <p className="text-text/80 font-montserrat">See what people are saying about our tours</p>
                    </a>
                  </div>
                </div>
                
                {/* Quick Actions Card */}
                <div className="premium-card border border-gold/40 hover:shadow-md transition-all duration-300">
                  <h3 className="text-2xl font-montserrat font-bold mb-4">Quick Actions</h3>
                  <div className="space-y-4">
                    <a 
                      href="/booking" 
                      className="bg-[#C58B65] text-white w-full flex items-center justify-center gap-2 py-3 rounded-md text-base font-montserrat font-semibold hover:bg-[#C58B65]/90 transition-all duration-300 hover:scale-105"
                    >
                      <Calendar size={20} />
                      <span>Book Your Tour</span>
                    </a>
                    
                    <a 
                      href="#faq-section" 
                      className="text-[#800020] font-montserrat font-semibold text-base hover:underline flex items-center justify-center"
                    >
                      <span>View Frequently Asked Questions</span>
                    </a>
                  </div>
                </div>
                
                {/* Contact Info Card */}
                <div className="premium-card border border-gold/40 hover:shadow-md transition-all duration-300">
                  <h3 className="text-2xl font-montserrat font-bold mb-4">Reach out</h3>
                  
                  <div className="flex items-start mb-6">
                    <Mail size={24} className="text-[#800020] mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-bold text-lg mb-1 font-montserrat">Email us</p>
                      <a href="mailto:catsabouttowntour@gmail.com" className="text-accent hover:underline font-montserrat">
                        catsabouttowntour@gmail.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Instagram size={24} className="text-[#800020] mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-bold text-lg mb-1 font-montserrat">Social Media</p>
                      <a href="https://www.instagram.com/catsabouttowntours/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-accent hover:underline font-montserrat">
                        @catsabouttowntours
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Social Proof Section */}
            <div className="mt-16 max-w-3xl mx-auto text-center">
              <h3 className="text-2xl font-montserrat font-bold mb-6">What Our Tour Guests Say</h3>
              <blockquote className="italic text-lg text-text/80 mb-4 font-montserrat">
                "Amazing tour guide in NYC to learn about cat history in different places across the city! Great for folks that love walking tours and CATS! Thanks guys for a great tour!!"
              </blockquote>
              <p className="font-semibold font-montserrat">— Aly S.</p>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-16 bg-neutral-tan" id="faq-section">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-center mb-8">Frequently asked questions</h2>
              <p className="text-center mb-8 text-lg font-montserrat">What would you like to know?</p>
              
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <FAQItem 
                    key={index}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={index === 0}
                  />
                ))}
              </div>
              
              <p className="text-center mt-10 text-lg font-montserrat">
                Still have questions? We've got answers. <a href="mailto:catsabouttowntour@gmail.com" className="text-accent hover:underline font-medium">Email us</a>.
              </p>
            </div>
          </div>
        </section>
        
        {/* Floating Help Button */}
        <FloatingHelpButton scrollToId="faq-section" />
        
        {/* Sticky Booking Button */}
        <StickyBookingButton link="/booking" />
      </div>
    </>
  );
};

export default Contact;
